import { NextSeo } from "next-seo";

import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import { HubHero } from "@/components/marketing/HubGrid";
import CaptionStyleGrid from "@/components/caption-styles/CaptionStyleGrid";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { hubGraph } from "@/lib/seo/graph";
import { HUBS } from "@/lib/seo/pages";
import { CAPTION_STYLES, captionStylePath } from "@/lib/caption-styles/content";

const PATH = HUBS.captionStyle.path;
const COUNT = CAPTION_STYLES.length;
const WORD_COUNT = CAPTION_STYLES.filter((style) => style.grouping === "word").length;
const LINE_COUNT = COUNT - WORD_COUNT;

const TITLE = `${COUNT} Animated Caption Styles for Shorts, Reels and TikTok`;
const SEO_TITLE = `${TITLE} | Crayo`;
const SEO_DESCRIPTION = `Browse all ${COUNT} Crayo caption templates: fonts, highlight colours, animations and boxes, with a note on which clips each one suits. Included on every plan.`;

const FAQS = [
  {
    question: "What is a caption style?",
    answer: `A caption style is a saved template for how your transcript looks on screen: the font, the text colour, how the spoken word or keywords are highlighted, the entrance and exit animation, and whether the text has a stroke, glow or box behind it. Crayo ships ${COUNT} of them, and picking one applies it to every line of the transcript at once.`,
  },
  {
    question: "What is the difference between one word and line styles?",
    answer: `${WORD_COUNT} of the styles show one word at a time, which suits fast speech and high-energy clips. The other ${LINE_COUNT} show a full line and mark the word being spoken with a colour, a fill or a reveal, which suits podcasts, interviews and educational clips. The editor has a grouping toggle that switches between the two sets.`,
  },
  {
    question: "Can I customise a style?",
    answer:
      "Yes. Every style is a starting point. Open Customise in the editor to change the font, the text and highlight colours, and where the captions sit on the frame. The change applies to the whole transcript, so you can match a style to your brand in one pass.",
  },
  {
    question: "Are the caption styles included in every plan?",
    answer:
      "Yes. Hobby, Clipper and Pro all include every caption style and the editor to customise them. There is no separate template purchase. Crayo itself is a paid subscription.",
  },
];

const CaptionStylesHub = () => {
  const crumbs = breadcrumbsForPath(PATH);

  return (
    <>
      <NextSeo title={SEO_TITLE} description={SEO_DESCRIPTION} />
      <JsonLd
        nodes={hubGraph({
          path: PATH,
          name: TITLE,
          description: SEO_DESCRIPTION,
          items: CAPTION_STYLES.map((style) => ({
            name: style.name,
            url: captionStylePath(style.slug),
            description: style.descriptor,
          })),
          faqs: FAQS,
        })}
      />

      <article className="w-full overflow-x-clip bg-white">
        <Breadcrumbs items={crumbs} />
        <HubHero
          eyebrow="Caption styles"
          title={TITLE}
          intro={`Every caption template in Crayo, described from its real settings: font, case, highlight, animation and background. ${WORD_COUNT} show one word at a time, ${LINE_COUNT} show full lines with the spoken word marked. Pick one, apply it to a clip, customise the colours if you like.`}
        />

        <div className="mx-auto w-full max-w-screen-xl px-5 py-12 sm:px-6 md:px-12 md:py-16 lg:px-20 lg:py-20">
          <CaptionStyleGrid styles={CAPTION_STYLES} />
        </div>

        <FAQ items={FAQS} />
      </article>

      <Footer />
    </>
  );
};

export default CaptionStylesHub;
